import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { subDays } from 'date-fns';
import { Suspense, useState } from 'react';
import { type DateRange } from 'react-day-picker';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';

import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { queryKeys } from '@/query-keys';

import { Metrics } from './metrics';

const defaultRange = {
  from: subDays(new Date(), 15),
  to: new Date(),
};

export function AnalyticsContent() {
  const [date, setDate] = useState<DateRange>(defaultRange);

  const {
    data: { analytics: analyticsData },
  } = useSuspenseQuery({
    queryKey: [...queryKeys.dashboard.analytics, date],
    queryFn: () =>
      SourcingService.getInternalApiSourcingDashboardAnalytics(
        (date.from ?? subDays(new Date(), 15)).toISOString(),
        (date.to ?? new Date()).toISOString(),
      ),
  });

  return (
    <>
      <div className="shadow-base flex w-full flex-col overflow-x-auto rounded-lg bg-white">
        <div className="flex flex-row items-center justify-between border-b border-neutral-200 px-4 py-1.5">
          <Typography variant="body2">
            <FormattedMessage
              defaultMessage="By time period"
              description="Label above date range for org-wide metrics"
            />
          </Typography>
          <DateRangePicker
            selectedDate={date}
            setSelectedDate={setDate}
            defaultRange={defaultRange}
          />
        </div>

        <Metrics analyticsData={analyticsData} />
      </div>
    </>
  );
}

export function Analytics() {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={<Analytics.Skeleton />}>
        <AnalyticsContent />
      </Suspense>
    </ErrorBoundary>
  );
}

Analytics.Skeleton = function AnalyticsSkeleton() {
  return (
    <>
      <div className="shadow-base flex w-full flex-col overflow-x-auto rounded-lg bg-white">
        <div className="flex h-[49px] flex-row items-center justify-between border-b border-neutral-200 px-4 py-1.5">
          <Typography variant="body2">
            <FormattedMessage
              defaultMessage="By time period"
              description="Label above date range for org-wide metrics"
            />
          </Typography>
          <Skeleton className="w-50 h-8" />
        </div>

        <Metrics.Skeleton />
      </div>
    </>
  );
};
